import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Paper,
  Button,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { FocusEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import { ProjectsPaginationResponseType } from 'pages/projects/hooks/useProjectsQuery';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADD_PROJECT } from 'constants/routes';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { ConfigEntryType } from 'config/config';
import useUserData from 'hooks/useUserData';
import { AddProjectModal } from '@buildwise/libs/projects';

type ProjectOptionType = { label: string; value: string };
type ProjectsSearchResponseType = ProjectsPaginationResponseType;

type PostProjectProps = {
  field: ConfigEntryType;
  readOnly?: boolean;
};

function PostProject({ field, readOnly }: PostProjectProps) {
  const {
    formState: { isSubmitting },
    getValues,
    setValue,
    control,
  } = useFormContext<PostFormData>();
  const { state } = useLocation();
  const [openAddProjectModal, setOpenAddProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const { canAddProjects, user, isNovaOrCompanyAdmin } = useUserData();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const projectId = (useWatch({ control })?.projectId || '') as string;
  const {
    data: projectsData,
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = useSearchItemQuery<ProjectsSearchResponseType>({
    searchType: 'projects',
    operator: 'or',
    filterBy: {
      filters: [
        {
          filterType: 'name',
          filterValue: 'ALL',
        },
      ],
    },
  });
  const { singularProjectsModuleName, pluralProjectsModuleName } = useBuildWiseModules();
  const [projectsOptions, selectedProject] = useMemo(() => {
    let project: { label: string; value: string } | null = null;

    if (projectId) {
      project = {
        label: projectId as string,
        value: projectId as string,
      };
    }

    const mappedProjects = (projectsData?.projects || []).map((projectData) => {
      if (projectId && projectData.id === projectId) {
        project = {
          label: projectData.name,
          value: projectData.id,
        };

        return project;
      }

      return {
        label: projectData.name,
        value: projectData.id,
      };
    });

    return [mappedProjects, project];
  }, [projectsData?.projects, projectId]);

  const handleSelectProject = (event: SyntheticEvent, project: ProjectOptionType | null) => {
    if (project) {
      setValue('projectId', project.value, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue('projectId', '', { shouldDirty: true, shouldValidate: true });
    }
  };

  useEffect(() => {
    if (state?.articleFormState) {
      refetchProjects();
    }
  }, [state?.articleFormState]);

  const handleOpenAddProjectModal = () => {
    setNewProjectName(inputValue);
    setOpenAddProjectModal(true);
  };

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState }) => (
          <FormControl fullWidth>
            <Autocomplete
              onChange={handleSelectProject}
              onInputChange={(_, value) => {
                setInputValue(value);
              }}
              onBlur={(event: FocusEvent) => {
                if (event.relatedTarget?.id === 'add-project' && canAddProjects) {
                  event.stopPropagation();
                  handleOpenAddProjectModal();
                  // navigate(ADD_PROJECT, {
                  //   state: {
                  //     articleFormState: getValues(),
                  //     projectName: inputValue,
                  //   },
                  // });
                }
              }}
              options={projectsOptions}
              autoHighlight
              value={selectedProject}
              noOptionsText={
                projectsLoading ? 'Loading...' : `No ${pluralProjectsModuleName} Found`
              }
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Select ${singularProjectsModuleName}`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {projectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                />
              )}
              disabled={isSubmitting || readOnly}
              {...(canAddProjects && {
                PaperComponent: ({ children, ...rest }) => (
                  <Paper {...rest}>
                    {children}
                    <Button
                      id="add-project"
                      startIcon={<AddLineIcon size={20} />}
                      sx={(theme) => ({ color: theme.palette.custom.link, m: 1 })}
                    >
                      Add {singularProjectsModuleName}
                    </Button>
                  </Paper>
                ),
              })}
            />
          </FormControl>
        )}
        name={field.entryCode}
      />

      {openAddProjectModal && (
        <AddProjectModal
          setOpenAddProjectModal={setOpenAddProjectModal}
          openAddProjectModal={openAddProjectModal}
          createProjectFromAddPostModal={{
            articleFormState: getValues(),
            projectName: newProjectName,
          }}
        />
      )}
    </>
  );
}

export default PostProject;
